import { Check } from '@mui/icons-material';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalDialog } from '../../../../../components/ModalDialog';
import { StyledInputLabel } from '../../../../../components/inputs/InputLabel';

const IMPORT_LAYOUT_ARTICLE_URL =
  'https://help.jst.scifree.se/hc/en-gb/articles/34766325582867-Journal-List-Import-Layout';

const EXAMPLE_HEADERS = [
  'Journal Title',
  'Imprint',
  'Print ISSN',
  'Electronic ISSN',
  'Journal URL',
  'Publishing Model',
  'License Options',
  'Subject Areas',
];

/** Link-style button that opens a modal explaining how to lay out the journals Excel file. */
const FileFormatHelp = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="link"
        size="small"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        sx={{
          mb: '2px',
          textTransform: 'none',
          fontSize: 14,
          color: 'var(--color-scifree-teal)',
        }}
      >
        {t('agreements.fileFormatHelp.openButton')}
      </Button>
      <ModalDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t('agreements.fileFormatHelp.title') as string}
        subtitle={t('agreements.fileFormatHelp.subtitle') as string}
        maxWidth="lg"
        actions={
          <Button variant="primary" onClick={() => setOpen(false)}>
            {t('agreements.fileFormatHelp.close')}
          </Button>
        }
      >
        <ul className="list-disc pl-6">
          <li>
            <Typography variant="body2">
              {t('agreements.fileFormatHelp.headerRow')}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t('agreements.fileFormatHelp.columnNames')}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t('agreements.fileFormatHelp.requiredValues')}
            </Typography>
          </li>
        </ul>
        <Typography
          id="file-format-example"
          variant="body2"
          sx={{ mt: 16, mb: '6px', fontWeight: 500 }}
        >
          {t('agreements.fileFormatHelp.example')}
        </Typography>
        {/* On narrow screens only the example scrolls sideways, not the whole modal.
            Focusable so keyboard users can scroll it too. */}
        <Box
          role="region"
          aria-labelledby="file-format-example"
          tabIndex={0}
          sx={{ maxWidth: '100%', overflowX: 'auto' }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
              '& th': {
                border: '1px solid',
                borderColor: 'divider',
                padding: '4px',
                textAlign: 'left',
                verticalAlign: 'top',
                fontWeight: 500,
                // Wrap between words only, never inside one ("Publishin" / "g Model").
                wordBreak: 'keep-all',
                overflowWrap: 'normal',
                hyphens: 'none',
              },
            }}
          >
            <thead>
              <tr>
                {EXAMPLE_HEADERS.map((header) => (
                  <th key={header} scope="col">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ mt: 16 }}>
          <Link
            href={IMPORT_LAYOUT_ARTICLE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('agreements.fileFormatHelp.moreInfo')}
            <span className="sr-only">
              {t('agreements.fileFormatHelp.opensInNewTab')}
            </span>
          </Link>
        </Typography>
      </ModalDialog>
    </>
  );
};

type FileUploadFieldProps = {
  value: string | File;
  disabled?: boolean;
  enableFilePreviews?: boolean;
  isLoading?: boolean;
  fileUpdatedBy?: string;
  fileUpdatedAt?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const FileUploadField = ({
  value,
  disabled = false,
  enableFilePreviews = false,
  isLoading = false,
  fileUpdatedBy,
  fileUpdatedAt,
  onChange,
}: FileUploadFieldProps) => {
  const { t } = useTranslation();

  const displayName = value instanceof File ? value.name : value;
  const hasFileInfo =
    !!value && !(value instanceof File) && (!!displayName || !!fileUpdatedBy);

  const fileIcon = isLoading ? (
    <CircularProgress size={20} sx={{ marginLeft: '14px', color: 'white' }} />
  ) : value ? (
    <Check sx={{ marginLeft: '14px' }} />
  ) : (
    <UploadIcon sx={{ marginLeft: '14px' }} />
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <StyledInputLabel id="file-label" htmlFor="file">
          Journals (.xlsx) <span aria-hidden="true">*</span>
          <span className="sr-only">required</span>
        </StyledInputLabel>
        <FileFormatHelp />
      </Box>
      {/* Screen readers only */}
      {hasFileInfo && (
        <span id="file-upload-status" className="sr-only">
          File uploaded
        </span>
      )}
      {hasFileInfo ? (
        <Box
          sx={{
            width: '100%',
            padding: '16px 14px',
            minHeight: '90px',
            borderRadius: 1,
            backgroundColor: 'var(--color-white)',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            ...(disabled && { opacity: 0.5 }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              mb: '4px',
            }}
          >
            <Typography
              id="file-name"
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {displayName}
            </Typography>
            <Check
              sx={{ marginLeft: 4, flexShrink: 0, color: 'success.main' }}
            />
          </Box>
          {fileUpdatedBy && fileUpdatedAt && (
            <Typography id="file-meta" variant="body2" sx={{ width: '100%' }}>
              {t('agreements.fileLastUpdatedByOn', {
                email: fileUpdatedBy,
                date: dayjs
                  .utc(fileUpdatedAt)
                  .local()
                  .format('YYYY-MM-DD HH:mm'),
              })}
            </Typography>
          )}
          <Button
            component="label"
            variant="link"
            size="small"
            role="button"
            tabIndex={disabled || isLoading ? -1 : 0}
            aria-labelledby="file-label file-upload-status file-name file-meta file-button-text"
            disabled={disabled || isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!disabled && !isLoading) e.currentTarget.click();
              }
            }}
            sx={{
              mt: '6px',
              textTransform: 'none',
              fontSize: 14,
              color: 'var(--color-scifree-teal)',
            }}
          >
            <input
              id="file"
              type="file"
              name="file"
              accept=".xlsx"
              onChange={onChange}
              disabled={disabled || isLoading}
              hidden
            />
            <span id="file-button-text">
              {t('agreements.clickToUploadNew')}
            </span>
            <UploadIcon sx={{ marginLeft: 4 }} />
          </Button>
        </Box>
      ) : (
        <Button
          component="label"
          variant="default"
          role="button"
          tabIndex={disabled || isLoading ? -1 : 0}
          aria-labelledby="file-label file-button-text"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!disabled && !isLoading) e.currentTarget.click();
            }
          }}
          sx={{
            width: '100%',
            '&.Mui-disabled': {
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-scifree-teal)',
              opacity: 0.5,
            },
          }}
          disabled={disabled || isLoading}
        >
          <input
            id="file"
            type="file"
            name="file"
            accept=".xlsx"
            onChange={onChange}
            disabled={disabled || isLoading}
            hidden
          />
          <span id="file-button-text">
            {isLoading
              ? t('agreements.uploadingFile')
              : value
                ? value instanceof File && fileUpdatedBy
                  ? t('agreements.newFileSelected')
                  : enableFilePreviews
                    ? t('agreements.fileUploadedPreview')
                    : t('agreements.fileUploaded')
                : t('agreements.button')}
          </span>
          {fileIcon}
        </Button>
      )}
    </>
  );
};
